import { useReorderItems } from 'components/WorkspaceEditor/hooks/useReorderItems'
import {
    useIsMoveBlockMenuDisplayed,
    useShallowHoveredItems,
} from 'components/WorkspaceEditor/HoveredItemStore'
import React, { memo, useCallback, useEffect } from 'react'
import { BestPracticesSettingPopover } from '../BestPracticesSettingPopover'
import { ManageOptionsPopover } from '../ManageOptionsPopover'
import MoveBlockPopover from '../MoveBlockPopover'
import { Workspace2Popover } from '../Workspace2Popover'
import { useMoveActionAvailability } from './hooks/useMoveActionAvailability'
import { TreeHandlers, TreeStates } from './types'

/**
 * Props for TreeViewPopovers component
 */
interface TreeViewPopoversProps extends TreeHandlers {
    /** Current state of the tree view including anchor elements */
    states: TreeStates
}

/**
 * TreeViewPopovers - Manages all popover menus for the tree view
 *
 * Handles menu hierarchy where closing submenus also closes the main menu.
 * Uses Material-UI's built-in backdrop handling where possible for simplicity.
 */
export const TreeViewPopovers = memo<TreeViewPopoversProps>(
    ({ states, ...handlers }) => {
        const containerRef = React.useRef<HTMLDivElement>(null)
        const { handleMoveBlock } = useReorderItems()

        // Menu states and actions from global store
        const {
            isMainMenuDisplayed,
            selectedMainMenuNode,
            isBPMenuDisplayed,
            isManageOptionsDisplayed,
            isNotesMenuDisplayed,
            setIsMoveBlockMenuDisplayed,
            setIsNotesMenuDisplayed,
            setIsBPMenuDisplayed,
            setIsManageOptionsDisplayed,
        } = useShallowHoveredItems((state) => ({
            isMainMenuDisplayed: state.isMainMenuDisplayed,
            selectedMainMenuNode: state.selectedMainMenuNode,
            isBPMenuDisplayed: state.isBPMenuDisplayed,
            isManageOptionsDisplayed: state.isManageOptionsDisplayed,
            isNotesMenuDisplayed: state.isNotesMenuDisplayed,
            setIsMoveBlockMenuDisplayed:
                state.actions.setIsMoveBlockMenuDisplayed,
            setIsNotesMenuDisplayed: state.actions.setIsNotesMenuDisplayed,
            setIsBPMenuDisplayed: state.actions.setIsBPMenuDisplayed,
            setIsManageOptionsDisplayed:
                state.actions.setIsManageOptionsDisplayed,
        }))

        const isMoveBlockMenuDisplayed = useIsMoveBlockMenuDisplayed()
        const { canMoveDown, canMoveUp } = useMoveActionAvailability()

        // =============================================
        // HANDLER FACTORY & FUNCTIONS
        // =============================================

        /**
         * Factory function to create handlers that close main menu after action.
         * Eliminates duplication of the "enhanced handler" pattern.
         */
        const createEnhancedHandler = useCallback(
            (originalHandler: () => void) => () => {
                originalHandler()
                handlers.onCloseMainMenu()
            },
            [handlers]
        )

        // Simplified handler using factory function
        const enhancedOnSaveManageOptions = createEnhancedHandler(
            handlers.onSaveManageOptions
        )

        // Move handlers also use the factory pattern for consistency
        const handleMoveUp = createEnhancedHandler(() => {
            handleMoveBlock('up')
            setIsMoveBlockMenuDisplayed(false)
        })

        const handleMoveDown = createEnhancedHandler(() => {
            handleMoveBlock('down')
            setIsMoveBlockMenuDisplayed(false)
        })

        /**
         * Outside Click Handler - Manages global document click detection
         *
         * Closes both main menu and all submenus when clicking outside the container.
         * This provides consistent behavior where any outside click dismisses the entire
         * menu system regardless of which specific menu elements are open.
         *
         * @behavior
         * - Only active when main menu is open (performance optimization)
         * - Detects clicks outside the main container
         * - Closes ALL menus (main and submenus) when outside click detected
         * - Ensures consistent dismissal behavior across all menu states
         */
        useEffect(() => {
            const handleOutsideClick = (event: MouseEvent) => {
                const target = event.target as Element

                // Ensure we have a valid target and container reference
                if (!containerRef.current || !target) return

                // Check if click is outside our main menu container
                const isOutsideContainer = !containerRef.current.contains(
                    target
                )

                if (isOutsideContainer) {
                    // Always close all menus when clicking outside the container
                    // This ensures both main menu and any open submenus are closed
                    handleMainMenuBackdrop()
                }
            }

            // Performance optimization: only attach listener when any menu is displayed
            if (
                isMainMenuDisplayed ||
                isBPMenuDisplayed ||
                isManageOptionsDisplayed ||
                isMoveBlockMenuDisplayed ||
                isNotesMenuDisplayed
            ) {
                document.addEventListener('mousedown', handleOutsideClick)
            }

            return () => {
                // Always cleanup to prevent memory leaks
                document.removeEventListener('mousedown', handleOutsideClick)
            }
        }, [
            containerRef,
            handlers.onCloseMainMenu,
            isMainMenuDisplayed,
            isBPMenuDisplayed,
            isManageOptionsDisplayed,
            isMoveBlockMenuDisplayed,
            isNotesMenuDisplayed,
            setIsBPMenuDisplayed,
            setIsManageOptionsDisplayed,
            setIsMoveBlockMenuDisplayed,
            setIsNotesMenuDisplayed,
        ])

        // =============================================
        // CONSOLIDATED STATE MANAGEMENT
        // =============================================

        /**
         * Centralized handler for backdrop clicks on main menu.
         * Material-UI popovers handle their own backdrop clicks via onClose props.
         * Closes all menus (main and submenus) for consistent behavior.
         */
        const handleMainMenuBackdrop = useCallback(() => {
            // Close all submenus
            setIsBPMenuDisplayed(false)
            setIsManageOptionsDisplayed(false)
            setIsMoveBlockMenuDisplayed(false)
            setIsNotesMenuDisplayed(false)
            handlers.onCloseMainMenu()
        }, [
            setIsBPMenuDisplayed,
            setIsManageOptionsDisplayed,
            setIsMoveBlockMenuDisplayed,
            setIsNotesMenuDisplayed,
            handlers,
        ])

        // =============================================
        // RENDER MENU COMPONENTS
        // =============================================

        return (
            <div
                ref={containerRef}
                onMouseDown={(event) => event.stopPropagation()} // Prevents clicks inside from bubbling up
            >
                {/* Main Workspace Menu - Entry point for all menu interactions */}
                {isMainMenuDisplayed && (
                    <Workspace2Popover
                        isActionPopoverDisplayed={isMainMenuDisplayed}
                        onCloseMainMenu={handleMainMenuBackdrop}
                        onClickManageOptions={handlers.onClickManageOptions}
                        onClickBPMenu={handlers.onClickBPMenu}
                        onClickNotesMenu={handlers.onClickNotesMenu}
                        onClickMoveBlockMenu={handlers.onClickMoveBlockMenu}
                        anchorElement={states.dragIconElement}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    />
                )}

                {/* Best Practices Settings Submenu
                    Uses external component that handles its own popover logic */}
                {isBPMenuDisplayed && (
                    <BestPracticesSettingPopover
                        anchorElement={states.menuElement}
                        isBestPractice={!!selectedMainMenuNode?.isBestPractice}
                        isGlobalBestPractice={
                            !!selectedMainMenuNode?.isGlobalBestPractice
                        }
                        isBestPracticeEditDisabled={false}
                        onCloseMainMenu={handleMainMenuBackdrop}
                    />
                )}

                {/* Manage Options Submenu
                    Material-UI Popover with onClose handling outside clicks */}
                {isManageOptionsDisplayed && (
                    <ManageOptionsPopover
                        anchorElement={states.menuElement}
                        isOpen={isManageOptionsDisplayed}
                        onClose={handleMainMenuBackdrop}
                        onSave={enhancedOnSaveManageOptions}
                    />
                )}

                {/* Move Block Submenu
                    Material-UI Popover with individual action handlers */}
                {isMoveBlockMenuDisplayed && (
                    <MoveBlockPopover
                        anchorElement={states.menuElement}
                        isOpen={isMoveBlockMenuDisplayed}
                        onClose={handleMainMenuBackdrop}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                        isMoveUpDisabled={canMoveUp}
                        isMoveDownDisabled={canMoveDown}
                    />
                )}
            </div>
        )
    }
)
